import queryString from 'query-string';
import chroma from 'chroma-js';

import { DesignDemoState, StyleByIdMap } from '..';
import { Layout } from '@graphql/types';
import { DemoFontOptionKey, LocationPalette, Device } from '../types';
import produce from 'immer';
import { ACCEPTED_LAYOUT_VALUES, BASE_FONT_KEYS, PREVIEW_FONT_KEYS, DEFAULT_PALETTE } from '../constants';
import merge from 'lodash-es/merge';
import { makeDebugger } from '@lib';

import { loadFont } from '@routes/event/guest/services/fonts/fontService';
const debug = makeDebugger('hydrateFromState.ts');
const couplePhotoBucket = 'https://joy-public-assets-bucket.s3-us-west-2.amazonaws.com/designShowcase/couples/';

const INITIAL_SECTIONS_STATE: DesignDemoState['sections'] = {
  themes: {
    open: false,
    category: null,
    showCategoryBuckets: false,
    useSuggestedBackgroundColor: true,
    themeKey: 'botanical_geo'
  },
  layout: { open: false, layout: Layout.aloha },
  colors: { open: false, hex: '#9AA493', displayString: 'Green', colorId: 'green' },
  fonts: { open: false, font: 'default' as DemoFontOptionKey, custom: null },
  advanced: { open: false }
};

const INITIAL_STATE: DesignDemoState = {
  hasLoaded: false,
  hasError: undefined,
  hasIframeError: undefined,
  hasLoadedPaletteFromSearch: false,
  showMobileNav: false,
  device: 'monitor' as Device,
  sections: INITIAL_SECTIONS_STATE,
  palette: DEFAULT_PALETTE,
  showCreateWeddingModal: false,
  assetId: `${couplePhotoBucket}Yx9VChouSU6w0zLs0fvp_w_couple_heather__joseph_carrying_meadow.jpg`,
  owner: 'Heather',
  fiancee: 'Joeseph',
  greeting: 'Welcome to our wedding website – so glad you could make it!'
};

export const hydrateStateFromSearch = (hasLoaded = false, styleByIdMap?: StyleByIdMap): DesignDemoState => {
  return produce(INITIAL_STATE, draft => {
    const values = queryString.parse(window.location.search);
    if (values.template && ACCEPTED_LAYOUT_VALUES.has(values.template as Layout)) {
      draft.sections.layout.layout = values.template as Layout;
    }

    draft.hasLoaded = hasLoaded;

    if (values.theme && styleByIdMap) {
      const searchTheme = values.theme as string;
      const style = styleByIdMap[searchTheme];
      if (style) {
        draft.sections.themes.themeKey = searchTheme;
      }
    }

    if (values.font && BASE_FONT_KEYS.includes(values.font as DemoFontOptionKey)) {
      if (!PREVIEW_FONT_KEYS.includes(values.font as DemoFontOptionKey)) {
        loadFont(values.font as DemoFontOptionKey);
        draft.sections.fonts.custom = values.font as DemoFontOptionKey;
      }
      draft.sections.fonts.font = values.font as DemoFontOptionKey;
    }

    if (values.palette) {
      let palette: LocationPalette;
      try {
        palette = merge(draft.palette, JSON.parse(values.palette as string));
        draft.palette = palette;
        const baseColor = palette.baseColor || DEFAULT_PALETTE.baseColor;
        const hexString = chroma([...baseColor.slice(0, 3).map(x => x * 255), baseColor[3]]).hex();
        draft.sections.colors.hex = hexString;
        draft.sections.colors.displayString = hexString === '#ffffff' ? 'White' : 'Custom Color';
        draft.hasLoadedPaletteFromSearch = true;
      } catch (e) {
        draft.sections.colors.hex = chroma(DEFAULT_PALETTE.baseColor).css();
        debug('Palette from URL is invalid.');
      }
    }

    if (values.assetId) {
      draft.assetId = `${couplePhotoBucket}${values.assetId}`;
    }

    if (values.owner) {
      draft.owner = typeof values.owner === 'string' ? values.owner : values.owner[0];
    }

    if (values.fiancee) {
      draft.fiancee = typeof values.fiancee === 'string' ? values.fiancee : values.fiancee[0];
    }

    if (values.greeting) {
      draft.greeting = typeof values.greeting === 'string' ? values.greeting : values.greeting[0];
    }
  });
};
